/* ---------------------------------------------------------------------------
 * Portal i18n resources — eager English, lazy active locale.
 *
 * Perf: the portal previously inlined all 22 locales × 2 namespaces into the
 * main JS chunk via `import.meta.glob({ eager: true })`. A user only ever needs
 * ONE locale plus the English fallback, so we now:
 *   1. Bundle English eagerly (the default + i18next fallbackLng).
 *   2. Lazy-load every non-English locale on demand. Vite code-splits each
 *      dynamic import into its own chunk, removing 21 locales' JSON from the
 *      main bundle.
 *
 * i18next is initialised synchronously with the `en` bundle; the active locale
 * is added post-init via `addResourceBundle`. `useSuspense: false` means missing
 * keys render the English fallback until the active locale chunk resolves (a
 * sub-second flash of English on first paint for non-en — acceptable for an
 * authenticated, no-index SPA).
 * --------------------------------------------------------------------------*/
import type { i18n as I18n } from 'i18next';
import { isSupportedLocale, DEFAULT_LOCALE, type SupportedLocale } from '@noxverify/i18n';

import enCommon from '../../../packages/i18n/locales/en/common.json';
import enPortal from '../../../packages/i18n/locales/en/portal.json';

/** Eager English resources used to initialise i18next synchronously. */
export const eagerEnResources = {
  en: {
    common: enCommon as Record<string, unknown>,
    portal: enPortal as Record<string, unknown>,
  },
};

// Lazy locale modules: `() => Promise<{ default: <json> }>`, keyed by abs path.
// `eager: false` (the default) makes Vite emit one chunk per imported module.
// English is bundled eagerly above, so each glob EXCLUDES `en/*.json` via Vite's
// negative-pattern array. Without the exclusion the en JSON is both statically
// imported and dynamically globbed, which Vite warns cannot be code-split.
const lazyCommon = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/common.json',
  '!../../../packages/i18n/locales/en/common.json',
]);
const lazyPortal = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/portal.json',
  '!../../../packages/i18n/locales/en/portal.json',
]);

function loaderFor(
  modules: Record<string, () => Promise<{ default: Record<string, unknown> }>>,
  locale: string,
): (() => Promise<{ default: Record<string, unknown> }>) | undefined {
  const suffix = `/locales/${locale}/`;
  for (const [path, load] of Object.entries(modules)) {
    if (path.includes(suffix)) return load;
  }
  return undefined;
}

const loaded = new Set<string>([DEFAULT_LOCALE]);

/**
 * Ensure the resource bundles for `locale` are present on the i18next instance.
 * No-op for English (bundled) or already-loaded locales. Adds the bundles and
 * re-emits `languageChanged` so subscribed components re-render with the new
 * strings once the dynamic import resolves.
 */
export async function ensureLocaleLoaded(i18n: I18n, locale: string): Promise<void> {
  if (loaded.has(locale) || !isSupportedLocale(locale)) return;
  loaded.add(locale);

  const [commonMod, portalMod] = await Promise.all([
    loaderFor(lazyCommon, locale)?.(),
    loaderFor(lazyPortal, locale)?.(),
  ]);

  if (commonMod) i18n.addResourceBundle(locale, 'common', commonMod.default, true, true);
  if (portalMod) i18n.addResourceBundle(locale, 'portal', portalMod.default, true, true);

  // Force dependent components to re-read translations now the bundle exists.
  if (i18n.resolvedLanguage === locale || i18n.language === locale) {
    i18n.emit('languageChanged', locale);
  }
}

/**
 * Wire lazy locale loading to i18next: load the active locale immediately and
 * on every subsequent `languageChanged`. Returns a cleanup function.
 */
export function attachLazyLocaleLoader(i18n: I18n): () => void {
  const onChange = (lng: string) => {
    void ensureLocaleLoaded(i18n, lng);
  };
  i18n.on('languageChanged', onChange);
  // Load whatever locale i18next currently resolves to (covers the initial
  // language set before this loader attached).
  const current = (i18n.resolvedLanguage ?? i18n.language) as SupportedLocale | undefined;
  if (current) void ensureLocaleLoaded(i18n, current);
  return () => i18n.off('languageChanged', onChange);
}
